import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { api } from '../lib/api';
import { formatPrice } from '../lib/currency';
import { ChevronLeft, ChevronRight, CalendarDays, Loader2 } from 'lucide-react';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const ym = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const parseYm = (s) => { const [y, m] = s.split('-').map(Number); return new Date(y, m - 1, 1); };
const addMonths = (s, n) => { const d = parseYm(s); d.setMonth(d.getMonth() + n); return ym(d); };

// Full-month price grid. Live Data API indicative fares — clicking a priced day picks it.
export const MonthPriceCalendar = ({ origin, destination, roundTrip = false, currency = 'INR', initialMonth, selectedDate, onPick }) => {
  const now = new Date();
  const currentMonth = ym(now);
  const maxMonth = ym(new Date(now.getFullYear(), now.getMonth() + 11, 1));
  const [month, setMonth] = useState(initialMonth || currentMonth);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (initialMonth) setMonth(initialMonth); }, [initialMonth]);

  const load = useCallback(() => {
    if (!origin || !destination) return;
    setLoading(true); setData(null);
    api.monthPrices({ origin, destination, month, round_trip: !!roundTrip, currency })
      .then(({ data }) => setData(data))
      .catch(() => setData({ days: [], has_data: false }))
      .finally(() => setLoading(false));
  }, [origin, destination, month, roundTrip, currency]);
  useEffect(() => { load(); }, [load]);

  const canPrev = month > currentMonth;
  const canNext = month < maxMonth;

  // price -> heat tone (green cheapest ... amber priciest), computed against this month's range.
  const toneFor = useMemo(() => {
    const min = data?.cheapest_price_inr, max = data?.max_price_inr;
    return (inr) => {
      if (inr == null || min == null || max == null || max <= min) return 'default';
      const r = (inr - min) / (max - min);
      if (r <= 0.25) return 'low';
      if (r <= 0.6) return 'mid';
      return 'high';
    };
  }, [data]);

  const toneCls = {
    low: 'text-emerald-600',
    mid: 'text-amber-600',
    high: 'text-rose-600',
    default: 'text-foreground',
  };

  const lead = data ? data.first_weekday : parseYm(month).getDay() === 0 ? 6 : parseYm(month).getDay() - 1;
  const [y, m] = month.split('-').map(Number);

  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid="month-price-calendar">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-accent" />
          <div className="flex flex-col">
            <span className="font-semibold text-sm leading-tight">{MONTH_NAMES[m - 1]} {y}</span>
            <span className="text-[10px] text-muted-foreground leading-tight" data-testid="month-cal-basis">
              {roundTrip ? 'Round-trip totals · cheapest return' : 'One-way fares'}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button" onClick={() => canPrev && setMonth(addMonths(month, -1))} disabled={!canPrev}
            data-testid="month-cal-prev"
            className={`h-8 w-8 grid place-items-center rounded-lg border transition-colors ${canPrev ? 'border-border hover:bg-secondary' : 'border-border/50 opacity-40 cursor-not-allowed'}`}
          ><ChevronLeft className="h-4 w-4" /></button>
          <button
            type="button" onClick={() => canNext && setMonth(addMonths(month, 1))} disabled={!canNext}
            data-testid="month-cal-next"
            className={`h-8 w-8 grid place-items-center rounded-lg border transition-colors ${canNext ? 'border-border hover:bg-secondary' : 'border-border/50 opacity-40 cursor-not-allowed'}`}
          ><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1.5 mb-1.5">
        {WEEKDAYS.map((w) => (
          <div key={w} className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground text-center py-1">{w}</div>
        ))}
      </div>

      {loading ? (
        <div className="grid place-items-center py-12"><Loader2 className="h-5 w-5 animate-spin text-accent" /></div>
      ) : (
        <>
          <div className="grid grid-cols-7 gap-1.5" data-testid="month-cal-grid">
            {Array.from({ length: lead }).map((_, i) => <div key={`lead-${i}`} />)}
            {(data?.days || []).map((d) => {
              const dayNum = Number(d.date.slice(8, 10));
              const hasPrice = d.price_inr != null;
              const disabled = d.is_past || !hasPrice;
              const isSelected = selectedDate === d.date;
              return (
                <button
                  key={d.date}
                  type="button"
                  onClick={() => !disabled && !isSelected && onPick?.(d.date)}
                  disabled={disabled || isSelected}
                  data-testid={`month-cal-day-${d.date}`}
                  className={`aspect-square rounded-xl border px-1 py-1 flex flex-col items-center justify-center text-center transition-colors
                    ${isSelected ? 'border-accent bg-accent/10 ring-1 ring-accent'
                      : d.is_cheapest ? 'border-emerald-500/50 bg-emerald-500/5 ring-1 ring-emerald-500/30 hover:bg-emerald-500/10'
                      : disabled ? 'border-transparent bg-secondary/30 cursor-not-allowed'
                      : 'border-border hover:border-accent/50 hover:bg-secondary cursor-pointer'}`}
                >
                  <span className={`text-[11px] leading-none ${d.is_past ? 'text-muted-foreground/50' : 'text-muted-foreground'}`}>{dayNum}</span>
                  <span className={`text-[10px] sm:text-[11px] font-semibold leading-tight mt-0.5 ${hasPrice ? (d.estimated ? 'text-muted-foreground' : toneCls[toneFor(d.price_inr)]) : 'text-muted-foreground/40'}`}>
                    {hasPrice ? `${d.estimated ? '~' : ''}${formatPrice(d.price_inr, currency, { compact: true })}` : '·'}
                  </span>
                </button>
              );
            })}
          </div>

          {data && !data.has_data && (
            <p className="text-xs text-muted-foreground text-center py-4" data-testid="month-cal-empty">No indicative fares cached for this month yet. Try another month.</p>
          )}
          {data?.has_data && (
            <div className="flex items-center gap-3 mt-3 pt-3 border-t border-border text-[10px] text-muted-foreground">
              <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-emerald-500/60" />Cheapest</span>
              <span className="flex items-center gap-1"><span className="text-emerald-600 font-semibold">•</span>Low</span>
              <span className="flex items-center gap-1"><span className="text-amber-600 font-semibold">•</span>Mid</span>
              <span className="flex items-center gap-1"><span className="text-rose-600 font-semibold">•</span>High</span>
              {data.has_estimates && <span className="flex items-center gap-1"><span className="font-semibold">~</span>Estimated</span>}
              {data.cheapest_date && <span className="ml-auto">Cheapest: <strong className="text-emerald-600">{formatPrice(data.cheapest_price_inr, currency)}</strong></span>}
            </div>
          )}
        </>
      )}
    </div>
  );
};
